import { useState, useMemo, useCallback, useEffect } from "react";
import type { ListingOverview, ListingQueryParams } from "@/types/listing";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Search, Plus } from "lucide-react";
import { Pagination } from "@/components/ui/pagination";
import { LicensePlate } from "@/components/my-auctions/LicensePlate";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import {
  getTypeColor,
  getTypeLabel,
  getVehicleStatusColor,
  getVehicleStatusLabel,
} from "@/utils/listingUtils";
import { VehicleTest } from "@/types/vehicleTest";
import { toast } from "sonner";
import { useListingManagement } from "@/hooks/useListingManagement";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useLanguage } from "@/context/LanguageContext";
import { adminLabels, commonButtonLabels } from "@/i18n";
import { usePersistedPageSize } from "@/hooks/usePersistedPageSize";

const STATUS_TABS = new Set([
  "all",
  "active",
  "completed",
  "cancelled",
  "reserved",
]);

interface ListingsViewProps {
  onCreateNew?: () => void;
  onListingClick?: (
    listingId: string,
    listingType: "auction" | "direct-purchase",
  ) => void;
  /** Deep-link status tab (e.g. push → reserved / completed). */
  initialStatus?: string;
}

export const ListingsView = ({
  onCreateNew,
  onListingClick,
  initialStatus,
}: ListingsViewProps) => {
  const { language } = useLanguage();
  const L =
    (adminLabels(language) as { leadboxListings?: Record<string, string> })
      ?.leadboxListings ?? {};
  const [searchTerm, setSearchTerm] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [activeMainTab, setActiveMainTab] = useState<string>("all");
  const [activeStatusTab, setActiveStatusTab] = useState<string>(() => {
    const s = (initialStatus || "").trim().toLowerCase();
    // "sold" alias used in some deep links
    if (s === "sold") return "completed";
    return STATUS_TABS.has(s) ? s : "all";
  });
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = usePersistedPageSize(
    "pageSize:listings",
    25,
    [10, 25, 50],
  );

  // Reset to first page when filters/search change
  useEffect(() => {
    setPage(1);
  }, [appliedSearch, activeMainTab, activeStatusTab]);

  const listingQueryParams = useMemo((): ListingQueryParams => {
    const params: ListingQueryParams = {
      page,
      limit: itemsPerPage,
      sort: "-createdAt",
    };
    if (appliedSearch.trim()) params.search = appliedSearch.trim();
    if (activeMainTab !== "all") {
      params.listingType = activeMainTab as ListingQueryParams["listingType"];
    }
    if (activeStatusTab !== "all") {
      params.status = activeStatusTab as ListingQueryParams["status"];
    }
    return params;
  }, [appliedSearch, activeMainTab, activeStatusTab, page, itemsPerPage]);

  const {
    getAllListingsWithParams,
    cancelListingAsync,
    activateReservedListingAsync,
    completeReservedListingAsync,
    isCancellingListing,
    isActivatingReservedListing,
    isCompletingReservedListing,
    getCancelListingPreview,
  } = useListingManagement({ enableGetAllListings: false });

  const {
    data: listingsData,
    isLoading: isLoadingListings,
    isFetching: isFetchingListings,
    error: listingsError,
  } = getAllListingsWithParams(listingQueryParams);

  const showInitialListingsLoader = isLoadingListings && !listingsData;

  // Cancel listing confirmation dialog state
  const [showCancelListingDialog, setShowCancelListingDialog] = useState(false);
  const [cancelListingInfo, setCancelListingInfo] = useState<{
    listingId: string;
    listingTitle: string;
    bidCount: number;
    hasActiveBids: boolean;
    listingType: string;
  } | null>(null);

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchTerm(e.target.value);
    },
    [],
  );

  const handleSearchSubmit = () => {
    setAppliedSearch(searchTerm.trim());
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") handleSearchSubmit();
  };

  const [selectedTest, setSelectedTest] = useState<VehicleTest | null>(null);

  const filteredListings = useMemo(() => {
    return listingsData?.listings || listingsData?.data || [];
  }, [listingsData]);

  const renderListingCard = (listing: ListingOverview) => {
    const isBuyNowTriggered =
      listing.auctionDetails?.isBuyNowTriggered === true;
    const auctionDisplayPrice =
      listing.status === "completed" ||
      listing.status === "reserved" ||
      listing.status === "ended"
        ? isBuyNowTriggered
          ? (listing.auctionDetails?.currentBuyNowPrice ??
            listing.auctionDetails?.buyNowFixed)
          : listing.auctionDetails?.currentBid
        : listing.auctionDetails?.currentBid;
    const displayPrice =
      listing.listingType === "auction"
        ? (auctionDisplayPrice ?? listing.auctionDetails?.startingPrice ?? 0)
        : listing.directPurchaseDetails?.currentPrice || 0;

    return (
      <Card
        key={`${listing.listingType}-${listing.id}`}
        className={`p-4 hover:shadow-md transition-shadow backdrop-blur ${
          onListingClick ? "cursor-pointer" : "cursor-default"
        }`}
        onClick={
          onListingClick
            ? () => onListingClick(listing.id, listing.listingType)
            : undefined
        }
      >
        <div className="flex items-center gap-4">
          {/* Thumbnail */}
          <div className="w-16 h-12 bg-gray-200 rounded-lg flex-shrink-0">
            <img
              src={listing.thumbnail}
              alt={
                listing.title ||
                `${listing.vehicleDetails?.make || "Unknown"} ${
                  listing.vehicleDetails?.model || "Unknown"
                }`
              }
              className="w-full h-full object-cover rounded-lg"
            />
          </div>

          {/* Vehicle Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h5 className="font-medium truncate">
                {listing.title ||
                  `${listing.vehicleDetails?.make || "Unknown"} ${
                    listing.vehicleDetails?.model || "Unknown"
                  }`}
              </h5>
              <span className="text-sm text-muted-foreground">
                ({listing.vehicleDetails?.year})
              </span>
            </div>
            <div className="text-sm text-muted-foreground mb-1">
              {listing.vehicleDetails?.make} {listing.vehicleDetails?.model}
            </div>
            <div className="flex items-center gap-2 mb-2">
              <LicensePlate
                lpn={listing.vehicleDetails?.regNumber || "Unknown"}
              />
              <Badge
                variant="outline"
                className={getTypeColor(listing.listingType)}
              >
                {getTypeLabel(listing.listingType, language)}
              </Badge>
              <Badge
                variant="outline"
                className={getVehicleStatusColor(listing.status)}
              >
                {getVehicleStatusLabel(listing.status, language)}
              </Badge>
            </div>
            <div className="flex items-center gap-4 text-sm text-muted-foreground">
              <span>{displayPrice} kr</span>
              <span>{listing.vehicleDetails?.mileage || 0} km</span>
              <span className="font-bold">Campaign</span>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2">
            {/* ACTIVE status: Avbryt button */}
            {listing.status === "active" && (
              <Button
                variant="destructive"
                size="sm"
                onClick={async (e) => {
                  e.stopPropagation();
                  try {
                    // Fetch cancel preview to get info about bids
                    const preview = await getCancelListingPreview(listing.id);
                    setCancelListingInfo({
                      listingId: listing.id,
                      listingTitle:
                        listing.title ||
                        `${listing.vehicleDetails?.make || "Unknown"} ${listing.vehicleDetails?.model || "Unknown"}`,
                      bidCount: preview.bidCount,
                      hasActiveBids: preview.hasActiveBids,
                      listingType: preview.listingType,
                    });
                    setShowCancelListingDialog(true);
                  } catch (error: any) {
                    toast.error(
                      L.cancelPreviewError ??
                        "Ett fel uppstod vid förhandsvisning av avbrytning",
                    );
                  }
                }}
                disabled={isCancellingListing}
              >
                {L.cancel ?? "Avbryt"}
              </Button>
            )}

            {/* RESERVED status: Aktivera, Slutför, Avsluta buttons */}
            {listing.status === "reserved" && (
              <>
                {/* Aktivera button - only if isAbleToActivate is true */}
                {listing.isAbleToActivate && (
                  <Button
                    variant="default"
                    size="sm"
                    onClick={async (e) => {
                      e.stopPropagation();
                      try {
                        await activateReservedListingAsync(listing.id);
                        toast.success(L.listingActivated ?? "Annons aktiverad");
                      } catch (error: any) {
                        const errorMessage =
                          error?.response?.data?.error ||
                          error?.message ||
                          (L.activateListingError ??
                            "Ett fel uppstod vid aktivering av annons");
                        toast.error(errorMessage);
                      }
                    }}
                    disabled={isActivatingReservedListing}
                  >
                    {L.activate ?? "Aktivera"}
                  </Button>
                )}

                {/* Slutför button */}
                <Button
                  variant="default"
                  size="sm"
                  onClick={async (e) => {
                    e.stopPropagation();
                    try {
                      await completeReservedListingAsync(listing.id);
                      toast.success(L.listingCompleted ?? "Annons slutförd");
                    } catch (error: any) {
                      const errorMessage =
                        error?.response?.data?.error ||
                        error?.message ||
                        (L.completeListingError ??
                          "Ett fel uppstod vid slutförande av annons");
                      toast.error(errorMessage);
                    }
                  }}
                  disabled={isCompletingReservedListing}
                >
                  {L.complete ?? "Slutför"}
                </Button>

                {/* Avsluta button */}
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={async (e) => {
                    e.stopPropagation();
                    try {
                      // Fetch cancel preview to get info about bids
                      const preview = await getCancelListingPreview(listing.id);
                      setCancelListingInfo({
                        listingId: listing.id,
                        listingTitle:
                          listing.title ||
                          `${listing.vehicleDetails?.make || "Unknown"} ${listing.vehicleDetails?.model || "Unknown"}`,
                        bidCount: preview.bidCount,
                        hasActiveBids: preview.hasActiveBids,
                        listingType: preview.listingType,
                      });
                      setShowCancelListingDialog(true);
                    } catch (error: any) {
                      toast.error(
                        "Ett fel uppstod vid förhandsvisning av avbrytning",
                      );
                    }
                  }}
                  disabled={isCancellingListing}
                >
                  {L.cancel ?? "Avbryt"}
                </Button>
              </>
            )}
          </div>
        </div>
      </Card>
    );
  };

  const listingsSearchHeader = (
    <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
      <div className="flex flex-1 max-w-xl gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
          <Input
            id="admin-listings-search"
            placeholder={L.searchListingsPlaceholder ?? "Sök annonser..."}
            value={searchTerm}
            onChange={handleSearchChange}
            onKeyDown={handleSearchKeyDown}
            className="pl-10"
          />
        </div>
        <Button
          type="button"
          variant="secondary"
          onClick={handleSearchSubmit}
          disabled={isFetchingListings}
          className="shrink-0"
        >
          {L.searchListingsButton ?? "Sök"}
        </Button>
      </div>
      <Button
        onClick={onCreateNew}
        size="sm"
        className="flex items-center gap-2"
      >
        <Plus className="h-4 w-4" />
        {L.createListing ?? "Skapa ny annons"}
      </Button>
    </div>
  );

  const totalCount = listingsData?.totalCount ?? 0;
  const totalPages = listingsData?.totalPages ?? 1;

  const listingsResultsSection = (
    <div className="space-y-4">
      <div className="text-sm text-primary/60">
        {L.listingsFound
          ?.replace("{count}", String(totalCount))
          ?.replace("{plural}", totalCount !== 1 ? "er" : "") ??
          `${totalCount} annons${totalCount !== 1 ? "er" : ""} hittade`}
        {appliedSearch && ` ${L.for ?? "för"} "${appliedSearch}"`}
        {isFetchingListings && !showInitialListingsLoader ? " …" : ""}
      </div>
      {showInitialListingsLoader ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4" />
          <p>{L.loadingListings ?? "Laddar annonser..."}</p>
        </div>
      ) : filteredListings.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground mt-4">
          {appliedSearch
            ? (L.noListingsFound ??
              "Inga annonser hittades med de valda filtren.")
            : (L.noListingsYet ?? "Inga annonser skapade än.")}
        </div>
      ) : (
        <div className="mt-4 space-y-4">
          <div className="grid grid-cols-1 gap-4">
            {filteredListings.map((listing) => renderListingCard(listing))}
          </div>
          {totalCount > itemsPerPage && (
            <Pagination
              currentPage={page}
              totalPages={totalPages}
              totalItems={totalCount}
              itemsPerPage={itemsPerPage}
              onPageChange={setPage}
              onItemsPerPageChange={(next) => {
                setItemsPerPage(next);
                setPage(1);
              }}
              itemsPerPageOptions={[10, 25, 50]}
            />
          )}
        </div>
      )}
    </div>
  );

  const statusTabs = (
    <Tabs
      value={activeStatusTab}
      onValueChange={setActiveStatusTab}
      className="w-full"
    >
      <div className="flex items-center justify-center">
        <TabsList className="grid w-auto grid-cols-5 backdrop-blur">
          <TabsTrigger value="all">{L.statusAll ?? "Alla"}</TabsTrigger>
          <TabsTrigger value="active">{L.statusActive ?? "Aktiva"}</TabsTrigger>
          <TabsTrigger value="completed">
            {L.statusCompleted ?? "Sålda"}
          </TabsTrigger>
          <TabsTrigger value="cancelled">
            {L.statusCancelled ?? "Avbrutna"}
          </TabsTrigger>
          <TabsTrigger value="reserved">
            {L.statusReserved ?? "Reserverade"}
          </TabsTrigger>
        </TabsList>
      </div>
      <TabsContent value={activeStatusTab} className="mt-4">
        {listingsResultsSection}
      </TabsContent>
    </Tabs>
  );

  return (
    <div className="space-y-4">
      {listingsSearchHeader}
      <Tabs
        value={activeMainTab}
        onValueChange={setActiveMainTab}
        className="w-full"
      >
        <TabsList className="grid w-auto grid-cols-3 backdrop-blur">
          <TabsTrigger value="all">{L.typeAll ?? "Alla"}</TabsTrigger>
          <TabsTrigger value="auction">
            {L.typeAuction ?? "Auktioner"}
          </TabsTrigger>
          <TabsTrigger value="direct-purchase">
            {L.typeDirectPurchase ?? "Direktköp"}
          </TabsTrigger>
        </TabsList>
      </Tabs>
      {statusTabs}

      {/* Cancel Listing Confirmation Dialog */}
      <AlertDialog
        open={showCancelListingDialog}
        onOpenChange={setShowCancelListingDialog}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {L.cancelListing ?? "Avbryt annons"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {cancelListingInfo && (
                <>
                  {L.cancelListingConfirmDesc?.replace(
                    "{title}",
                    cancelListingInfo.listingTitle,
                  ) ??
                    `Du håller på att avbryta annonsen "${cancelListingInfo.listingTitle}". Detta går inte att ångra!`}
                  <br />
                  <br />
                  {cancelListingInfo.hasActiveBids &&
                  cancelListingInfo.bidCount > 0 ? (
                    <>
                      <div className="mt-4 p-3 border border-yellow-400 bg-yellow-50 dark:bg-yellow-900/20 dark:border-yellow-800 rounded-md text-yellow-800 dark:text-yellow-200">
                        <p className="font-semibold">
                          {L.important ?? "Viktigt:"}
                        </p>
                        <p className="mt-2">
                          {L.importantBidsNotice?.replace(
                            "{count}",
                            cancelListingInfo.bidCount.toString(),
                          ) ??
                            `Denna auktion är aktiv och har ${cancelListingInfo.bidCount} bud. Om du avbryter annonsen kommer alla som har budat få en notis om att annonsen avbrutits.`}
                        </p>
                      </div>
                      <br />
                    </>
                  ) : null}
                  {L.sellerWillBeNotified ??
                    "Säljaren kommer att få en notis (email + in-app) om att annonsen avbrutits."}
                  {cancelListingInfo?.hasActiveBids &&
                    cancelListingInfo.bidCount > 0 && (
                      <>
                        {" "}
                        {L.biddersWillBeNotified ??
                          "Alla budgivare kommer också att få en notis."}
                      </>
                    )}
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isCancellingListing}>
              {L.cancel ?? "Avbryt"}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                if (!cancelListingInfo) return;
                try {
                  await cancelListingAsync(cancelListingInfo.listingId);
                  toast.success(
                    L.listingCancelled ?? "Annonsen har avbrutits!",
                  );
                  setShowCancelListingDialog(false);
                  setCancelListingInfo(null);
                } catch (error: unknown) {
                  const errorMessage =
                    error instanceof Error
                      ? error.message
                      : (L.cancelListingError ??
                        "Ett fel uppstod vid avbrytning av annons");
                  toast.error(errorMessage);
                }
              }}
              disabled={isCancellingListing}
              className="bg-red-600 hover:bg-red-700"
            >
              {isCancellingListing
                ? (L.cancelling ?? "Avbryter...")
                : (L.yesCancelListing ?? "Ja, avbryt annonsen")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
